import { useEffect, useRef, useState } from 'react';
import { clsx } from '../../ds/clsx';
import { usePrefersReducedMotion } from '../../ds/hooks';
import styles from './LevelMeter.module.css';

/**
 * Audio level of the running call (P-45): an `AnalyserNode` per stream (microphone and the remote side),
 * the louder RMS of both drives five bars — the „Pegel" recipe of Beratung 10 (speech lights three of five).
 * Muted, the microphone is left out. With reduced motion a static dot says „spricht" instead of moving bars.
 */

const BARS = 5;
/** dBFS of the first bar and the step per bar: speech at about −30 dBFS lights three bars. */
const FLOOR_DB = -57;
const STEP_DB = 9;

let sharedContext: AudioContext | null = null;

function audioContext(): AudioContext | null {
  if (typeof window === 'undefined' || typeof window.AudioContext === 'undefined') return null;
  sharedContext ??= new window.AudioContext();
  if (sharedContext.state === 'suspended') void sharedContext.resume();
  return sharedContext;
}

interface Tap {
  source: MediaStreamAudioSourceNode;
  analyser: AnalyserNode;
  buffer: Float32Array<ArrayBuffer>;
}

function tapOf(context: AudioContext, stream: MediaStream | null): Tap | null {
  if (stream === null || stream.getAudioTracks().length === 0) return null;
  const source = context.createMediaStreamSource(stream);
  const analyser = context.createAnalyser();
  analyser.fftSize = 1024;
  analyser.smoothingTimeConstant = 0.6;
  source.connect(analyser);
  return { source, analyser, buffer: new Float32Array(analyser.fftSize) };
}

function rmsOf(tap: Tap | null): number {
  if (tap === null) return 0;
  tap.analyser.getFloatTimeDomainData(tap.buffer);
  let sum = 0;
  for (const sample of tap.buffer) sum += sample * sample;
  return Math.sqrt(sum / tap.buffer.length);
}

/** RMS (0–1) → lit bars (0–5). */
export function barsFor(rms: number): number {
  if (rms <= 0) return 0;
  const db = 20 * Math.log10(rms);
  return Math.max(0, Math.min(BARS, Math.floor((db - FLOOR_DB) / STEP_DB) + 1));
}

export interface LevelMeterProps {
  local: MediaStream | null;
  remote: MediaStream | null;
  /** Measures only while true (the call is connected). */
  active: boolean;
  /** The microphone is muted: only the remote side counts. */
  muted?: boolean | undefined;
  /** `inverse` on navy (queue bar, call bar), `paper` on cards. */
  tone?: 'inverse' | 'paper' | undefined;
  className?: string | undefined;
}

export function LevelMeter({
  local,
  remote,
  active,
  muted = false,
  tone = 'inverse',
  className,
}: LevelMeterProps) {
  const reducedMotion = usePrefersReducedMotion();
  const [level, setLevel] = useState(0);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;

  useEffect(() => {
    if (!active) {
      setLevel(0);
      return;
    }
    const context = audioContext();
    if (context === null) return;
    const localTap = tapOf(context, local);
    const remoteTap = tapOf(context, remote);
    let frame = 0;
    let last = -1;
    const loop = () => {
      const rms = Math.max(mutedRef.current ? 0 : rmsOf(localTap), rmsOf(remoteTap));
      const bars = barsFor(rms);
      if (bars !== last) {
        last = bars;
        setLevel(bars);
      }
      frame = window.requestAnimationFrame(loop);
    };
    frame = window.requestAnimationFrame(loop);
    return () => {
      window.cancelAnimationFrame(frame);
      for (const tap of [localTap, remoteTap]) {
        if (tap === null) continue;
        tap.source.disconnect();
        tap.analyser.disconnect();
      }
    };
  }, [active, local, remote]);

  const speaking = level >= 2;
  if (reducedMotion) {
    return (
      <span
        className={clsx(styles.static, tone === 'paper' && styles.paper, className)}
        role="meter"
        aria-label="Pegel"
        aria-valuemin={0}
        aria-valuemax={BARS}
        aria-valuenow={level}
      >
        <span className={clsx(styles.dot, speaking && styles.dotOn)} aria-hidden="true" />
        {speaking ? 'spricht' : 'still'}
      </span>
    );
  }
  return (
    <span
      className={clsx(styles.meter, tone === 'paper' && styles.paper, className)}
      role="meter"
      aria-label="Pegel"
      aria-valuemin={0}
      aria-valuemax={BARS}
      aria-valuenow={level}
    >
      {Array.from({ length: BARS }, (_, index) => (
        <span key={index} className={clsx(styles.bar, index < level && styles.barOn)} aria-hidden="true" />
      ))}
    </span>
  );
}
